// Lingua del sito (italiano, inglese…): scelta, salvata e applicata.
//
// - La lingua è quella scelta col selettore (header e footer), ricordata nel
//   browser; un link con ?lang=en la imposta (e la ricorda). Senza scelta il
//   sito è in italiano: niente lingua del browser, così chi indicizza il sito
//   (e chi ha il browser in inglese ma cerca casa in Italia) lo vede com'è.
// - I testi restano in italiano nel codice: la pagina si traduce mentre Vue la
//   disegna (domTranslator.js) col dizionario della lingua (locales/*.json).
//   t() serve ai pochi testi che non finiscono nella pagina (alert, titoli).
import { reactive } from 'vue';
import { Dictionary, EMPTY_DICTIONARY } from './dictionary';
import { createDomTranslator } from './domTranslator';
import { LANGUAGES, SOURCE_LANGUAGE, languageFromCode } from './languages';

export { LANGUAGES, SOURCE_LANGUAGE, languageFromCode };

export const STORAGE_KEY = 'immaginacasa.lang';
export const QUERY_PARAM = 'lang';

export const i18nState = reactive({ locale: SOURCE_LANGUAGE, loading: false });

let dictionary = EMPTY_DICTIONARY;
let translator = null;

// I dizionari si scaricano solo quando servono (un chunk per lingua).
export const loaders = {
  en: () => import(/* webpackChunkName: "i18n-en" */ './locales/en.json'),
};

export async function loadDictionary(code) {
  const load = loaders[code];
  if (!load) return EMPTY_DICTIONARY;
  const mod = await load();
  return new Dictionary(mod.default || mod);
}

function readStored() {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

function store(code) {
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch (e) {
    // Senza storage (finestra privata, dati bloccati) la scelta vale per la pagina.
  }
}

// La lingua di partenza: ?lang= nell'indirizzo, poi quella ricordata, poi
// l'italiano.
export function resolveInitialLocale({ search = '', stored = null } = {}) {
  let fromQuery = null;
  try {
    fromQuery = new URLSearchParams(search).get(QUERY_PARAM);
  } catch (e) {
    fromQuery = null;
  }
  const lang = languageFromCode(fromQuery) || languageFromCode(stored);
  return lang ? lang.code : SOURCE_LANGUAGE;
}

// [text] (italiano) nella lingua del sito; {nome} prende i valori di args.
export function t(text, args) {
  let out = dictionary.translate(text);
  if (args) {
    for (const [key, value] of Object.entries(args)) {
      out = out.split(`{${key}}`).join(value == null ? '' : String(value));
    }
  }
  return out;
}

function getTranslator() {
  if (!translator && typeof document !== 'undefined') {
    translator = createDomTranslator({
      root: document.body,
      titleElement: document.querySelector('title'),
    });
  }
  return translator;
}

function applyDocumentLang(code) {
  if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', code);
}

// Cambia la lingua del sito, subito, senza ricaricare la pagina.
export async function setLocale(code, { persist = true } = {}) {
  const lang = languageFromCode(code);
  if (!lang) return;
  if (persist) store(lang.code);
  if (lang.code === i18nState.locale) return;
  i18nState.loading = true;
  try {
    const next = lang.code === SOURCE_LANGUAGE ? EMPTY_DICTIONARY : await loadDictionary(lang.code);
    dictionary = next;
    i18nState.locale = lang.code;
    applyDocumentLang(lang.code);
    const tr = getTranslator();
    if (tr) {
      tr.stop();
      if (!next.isEmpty) tr.start((s) => next.translate(s));
    }
  } catch (e) {
    // Un dizionario che non arriva (rete, deploy in corso) lascia l'italiano.
    dictionary = EMPTY_DICTIONARY;
    i18nState.locale = SOURCE_LANGUAGE;
    applyDocumentLang(SOURCE_LANGUAGE);
  } finally {
    i18nState.loading = false;
  }
}

// All'avvio, prima di montare l'app: la lingua dall'indirizzo o dal browser.
// Con ?lang= la scelta si ricorda.
export async function initI18n() {
  if (typeof window === 'undefined') return;
  const search = window.location.search;
  const code = resolveInitialLocale({ search, stored: readStored() });
  const fromQuery = languageFromCode(new URLSearchParams(search).get(QUERY_PARAM));
  if (code === SOURCE_LANGUAGE && !fromQuery) return;
  await setLocale(code, { persist: Boolean(fromQuery) });
}

// $t nei template e i18nState per chi deve sapere la lingua.
export const i18nPlugin = {
  install(app) {
    app.config.globalProperties.$t = t;
    app.config.globalProperties.$i18n = i18nState;
    app.provide('i18n', i18nState);
  },
};
